import { ROOT_HIERARCHY_ID } from 'constants/hierarchy';
import { EntityStorages, MapEntity } from 'types';

import { createHierarchy, updateHierarchyProperty } from 'utils';

import { initialState } from '../constants';
import {
  Entities,
  EntitiesState,
  HandleMapEntitiesBulkUpdate,
  HandleMapEntitiesGetPayload,
  HandleMapEntityChildrenGetPayload,
  HandleMapEntityCreatePayload,
  ProcessMapEntityFn,
} from '../types';

import { insertCounters } from './counters';
import {
  getMapEntities,
  getMapEntityTemplateIds,
  processCreatedMapEntity,
  processExistingMapEntity,
} from './entities';

export const handleMapEntitiesGet = (
  state: EntitiesState,
  payload: HandleMapEntitiesGetPayload,
  entityState: MapEntity['state'],
  processEntityFn?: ProcessMapEntityFn,
  shouldReplaceCounters?: boolean
) => {
  const { entities, hierarchy, counters } = payload;
  const { mapLayerTemplateId, mapObjectTemplateId } = getMapEntityTemplateIds(
    state.predefinedTemplates
  );

  return {
    entities: getMapEntities(
      mapLayerTemplateId,
      mapObjectTemplateId,
      entities,
      entityState,
      processEntityFn
    ),
    hierarchy: createHierarchy(ROOT_HIERARCHY_ID, hierarchy),
    counters: shouldReplaceCounters
      ? counters
      : insertCounters(state.counters, counters),
  };
};

export const handleMapEntityChildrenGet = (
  state: EntitiesState,
  payload: HandleMapEntityChildrenGetPayload,
  entityState: MapEntity['state'],
  processExistingEntity?: (
    entities: Entities,
    mapEntity: MapEntity,
    parentId: number
  ) => MapEntity
) => {
  const { entities, hierarchy, parentId } = payload;
  const { mapLayerTemplateId, mapObjectTemplateId } = getMapEntityTemplateIds(
    state.predefinedTemplates
  );
  const { id, children } = hierarchy?.[0] ?? initialState.hierarchy;
  const currentEntityId = parentId ?? id;
  const entityChildrenMap = getMapEntities(
    mapLayerTemplateId,
    mapObjectTemplateId,
    entities,
    entityState,
    processExistingEntity
      ? (mapEntity) =>
          processExistingEntity(state.entities, mapEntity, currentEntityId)
      : undefined
  );

  return {
    entities: {
      ...state.entities,
      ...entityChildrenMap,
    },
    hierarchy: updateHierarchyProperty(
      state.hierarchy,
      currentEntityId,
      'children',
      children
    ),
  };
};

export const handleMapEntityCreate = (
  state: EntitiesState,
  payload: HandleMapEntityCreatePayload
) => {
  const { entities, hierarchy, counters, targetId } = payload;
  const { id, children } = hierarchy?.[0] ?? initialState.hierarchy;
  const { mapLayerTemplateId, mapObjectTemplateId } = getMapEntityTemplateIds(
    state.predefinedTemplates
  );

  const prevEntities = Object.values(state.entities);
  const updatedEntities = getMapEntities(
    mapLayerTemplateId,
    mapObjectTemplateId,
    [...prevEntities, ...entities],
    { storage: EntityStorages.FULL, active: false },
    (mapEntity) => processCreatedMapEntity(state.entities, mapEntity, targetId)
  );

  const updatedHierarchy = updateHierarchyProperty(
    state.hierarchy,
    id,
    'children',
    children
  );

  return {
    entities: updatedEntities,
    hierarchy: updatedHierarchy,
    counters: insertCounters(state.counters, counters),
  };
};

export const handleMapEntitiesBulkUpdate = (
  state: EntitiesState,
  payload: HandleMapEntitiesBulkUpdate
) => {
  const { entities, hierarchy, parentId } = payload;
  const { mapLayerTemplateId, mapObjectTemplateId } = getMapEntityTemplateIds(
    state.predefinedTemplates
  );
  const { id, children } = hierarchy?.[0] ?? initialState.hierarchy;
  const currentEntityId = parentId ?? id;
  const entityChildrenMap = getMapEntities(
    mapLayerTemplateId,
    mapObjectTemplateId,
    entities,
    { storage: EntityStorages.FULL, active: false },
    (mapEntity) =>
      processExistingMapEntity(state.entities, mapEntity, {
        active: true,
      })
  );

  return {
    entities: {
      ...state.entities,
      ...entityChildrenMap,
    },
    hierarchy: updateHierarchyProperty(
      state.hierarchy,
      currentEntityId,
      'children',
      children
    ),
  };
};
